import React, { useEffect, useState } from "react";
import axios from "axios";
import { Alert, Box, Chip, Stack, Typography } from "@mui/material";
import AIPromptTextField from "./AIPromptTextField";
import { API_ENDPOINT } from "../../../utils/constants";
import { getHeader } from "../../../utils/util";

const DatabaseAIAssistant = ({
  groupId,
  columns = [],
  onSuccess,
  onError,
  placeholder = "Ask AI to help create your list",
}) => {
  const [aiPrompt, setAiPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [resultCount, setResultCount] = useState(null);
  const availableColumns = Array.isArray(columns) ? columns : [];

  useEffect(() => {
    setAiPrompt("");
    setResultCount(null);
  }, [groupId]);

  const handleSendPrompt = async (promptText) => {
    if (!groupId) return;

    try {
      setLoading(true);
      setResultCount(null);
      const payload = {
        prompt: promptText,
        id: groupId,
      };

      const response = await axios.post(
        `${API_ENDPOINT}/database/database-ai-assistant`,
        payload,
        getHeader()
      );

      setResultCount(response.data.count);
      if (onSuccess) onSuccess(response.data);
    } catch (error) {
      setResultCount(null);
      if (onError) onError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {availableColumns.length > 0 && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            gutterBottom
            fontWeight={500}
            color="text.secondary"
          >
            Available Contact Fields:
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {availableColumns.map((column, index) => (
              <Chip
                key={`${column}-${index}`}
                label={column}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ bgcolor: "common.white" }}
              />
            ))}
          </Stack>
        </Box>
      )}

      <AIPromptTextField
        value={aiPrompt}
        onChange={setAiPrompt}
        placeholder={placeholder}
        size="large"
        loading={loading}
        variant="outlined"
        onSend={handleSendPrompt}
        suggestions={[]}
      />

      {resultCount != null && (
        <Alert
          severity={resultCount > 0 ? "success" : "warning"}
          variant="outlined"
          sx={{
            mt: 2,
            borderRadius: 2,
            alignItems: "center",
            bgcolor: resultCount > 0 ? "#f0fdf4" : "#fffbeb",
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            AI audience selection
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
            {resultCount.toLocaleString("en-IN")}
            <Typography component="span" variant="body2" sx={{ ml: 1 }}>
              {resultCount === 1 ? "contact matched" : "contacts matched"}
            </Typography>
          </Typography>
          {resultCount === 0 && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              No contacts match your request. Try adjusting your prompt.
            </Typography>
          )}
        </Alert>
      )}
    </Box>
  );
};

export default DatabaseAIAssistant;
